import { animated, useSpringValue, useTransition } from '@react-spring/web'
import {
  ChevronDown,
  ChevronsLeftRight,
  ChevronsRight,
  ChevronsRightLeft,
  ChevronUp,
  Settings,
} from 'lucide-react'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import { Switch } from 'slab'

import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'

const DISTANCE_FROM_TOP = 140
const DRAWER_WIDTH = 450
const DRAWER_WIDTH_WIDENED = 800

const BUTTON_WIDTH = 20
const EXPAND_BUTTON_HEIGHT = 40
const HEADER_HEIGHT = 60 // Height of header when present (padding + content)

const OPEN_BUTTON_OFFSET = 16
const PADDING_RIGHT = 10

const VIEW_HEIGHT_COLLAPSED = 60 // 60vh.
const VIEW_HEIGHT_EXPANDED = 100 // 100vh.

const HEIGHT_BUFFER = DISTANCE_FROM_TOP + EXPAND_BUTTON_HEIGHT + 20 // 20px for padding.

type ChildProps = {
  children?: React.ReactNode
  statusComponent?: React.ReactNode
  headerComponent?: React.ReactNode
}

// Add draft content state to the context
type DraftContentState = {
  [key: string]: {
    content: string
    timestamp: number
  }
}

export const DrawerContext = createContext({
  isOpen: false,
  setIsOpen: (_: boolean) => {},
  isExpanded: false,
  setIsExpanded: (_: boolean) => {},
  isWidened: false,
  setIsWidened: (_: boolean) => {},
  toggleDrawer: () => {},
  settings: { openByDefault: true, widenByDefault: false },
  updateSettings: (_: Partial<DrawerSettings>) => {},
  // Draft content management
  getDraftContent: (_key: string): string => '',
  setDraftContent: (_key: string, _content: string) => {},
  clearDraftContent: (_key: string) => {},
  cleanupOldDrafts: () => {},
})

type DrawerSettings = {
  openByDefault: boolean
  widenByDefault: boolean
}

function useDrawerProvider () {
  const [settings, setSettings] = useLocalStorage<DrawerSettings>('drawer-settings', {
    openByDefault: true,
    widenByDefault: false,
  })

  const [draftContent, setDraftContentState] = useLocalStorage<DraftContentState>('drawer-draft-content-v3', {})

  const [isOpen, setIsOpen] = useState(settings.openByDefault)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isWidened, setIsWidened] = useState(settings.widenByDefault)

  const toggleDrawer = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  const setIsExpandedStable = useCallback((value: boolean) => {
    setIsExpanded(value)
  }, [])

  const updateSettings = useCallback((newSettings: Partial<DrawerSettings>) => {
    const updatedSettings = { ...settings, ...newSettings }
    setSettings(updatedSettings)

    // Only apply width setting immediately - open setting should only affect future mounts
    if (newSettings.widenByDefault !== undefined) {
      setIsWidened(newSettings.widenByDefault)
    }
  }, [settings, setSettings])

  // Draft content management functions
  const getDraftContent = useCallback((key: string): string => {
    const draft = draftContent[key]
    return draft?.content || ''
  }, [draftContent])

  const setDraftContent = useCallback((key: string, content: string) => {
    setDraftContentState(prev => ({
      ...prev,
      [key]: { content, timestamp: Date.now() }
    }))
  }, [setDraftContentState])

  const clearDraftContent = useCallback((key: string) => {
    setDraftContentState(prev => {
      const newState = { ...prev }
      delete newState[key]
      return newState
    })
  }, [setDraftContentState])

  const cleanupOldDrafts = useCallback(() => {
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000) // 24 hours in milliseconds
    setDraftContentState(prev => {
      const newState: DraftContentState = {}
      Object.entries(prev).forEach(([key, draft]) => {
        // Keep drafts that are less than 24 hours old
        if (draft.timestamp > oneDayAgo) {
          newState[key] = draft
        }
      })
      return newState
    })
  }, [setDraftContentState])

  return useMemo(() => ({
    isOpen,
    isExpanded,
    setIsOpen,
    setIsExpanded: setIsExpandedStable,
    isWidened,
    setIsWidened,
    toggleDrawer,
    settings,
    updateSettings,
    getDraftContent,
    setDraftContent,
    clearDraftContent,
    cleanupOldDrafts,
  }), [isOpen, isExpanded, isWidened, toggleDrawer, settings, updateSettings, setIsExpandedStable, getDraftContent, setDraftContent, clearDraftContent, cleanupOldDrafts])
}

export function DrawerProvider ({ children, statusComponent, headerComponent }: ChildProps) {
  const value = useDrawerProvider()

  return (
    <DrawerContext.Provider value={value}>
      <Drawer statusComponent={statusComponent} headerComponent={headerComponent}>{children}</Drawer>
    </DrawerContext.Provider>
  )
}

export function useDrawer () {
  return useContext(DrawerContext)
}

const DrawerSettingsPopover = () => {
  const { settings, updateSettings } = useDrawer()
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  return (
    <Popover open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
      <PopoverTrigger asChild>
        <button
          className='p-2 rounded-md hover:bg-gray-100 transition-colors'
          aria-label='Drawer settings'
        >
          <Settings size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64' align='end'>
        <div className='space-y-4'>
          <div className='font-medium text-sm text-gray-900'>Drawer Settings</div>

          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <label className='text-sm text-gray-700'>Open by default</label>
              <Switch
                checked={settings.openByDefault}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateSettings({ openByDefault: e.target.checked })}
              />
            </div>

            <div className='flex items-center justify-between'>
              <label className='text-sm text-gray-700'>Wide by default</label>
              <Switch
                checked={settings.widenByDefault}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateSettings({ widenByDefault: e.target.checked })}
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function Drawer ({ children, statusComponent, headerComponent }: ChildProps) {
  const {
    isOpen,
    isExpanded,
    setIsExpanded,
    isWidened,
    setIsWidened,
    toggleDrawer,
  } = useDrawer()
  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const toggleWiden = () => {
    setIsWidened(!isWidened)
  }

  const drawerTransition = useTransition(isOpen, {
    from: {
      transform: `translateX(${
        (isWidened ? DRAWER_WIDTH_WIDENED : DRAWER_WIDTH) + OPEN_BUTTON_OFFSET
      }px)`,
    },
    enter: { transform: 'translateX(0px)' },
    leave: {
      transform: `translateX(${
        (isWidened ? DRAWER_WIDTH_WIDENED : DRAWER_WIDTH) + OPEN_BUTTON_OFFSET
      }px)`,
    },
  })

  const buttonTransform = useSpringValue(
    `translateX(${DRAWER_WIDTH - BUTTON_WIDTH}px)`
  )
  const chevronRotation = useSpringValue(180)

  // Calculate dynamic height buffer based on whether header is present
  const dynamicHeightBuffer = HEIGHT_BUFFER + (headerComponent ? HEADER_HEIGHT : 0)

  const drawerHeight = useSpringValue(
    `calc(${VIEW_HEIGHT_COLLAPSED}vh - ${dynamicHeightBuffer}px)`
  )
  const animatedDrawerWidth = useSpringValue(DRAWER_WIDTH)

  // Position for expansion button (below the drawer)
  const expandButtonTop = useSpringValue(
    `calc(${DISTANCE_FROM_TOP}px + ${VIEW_HEIGHT_COLLAPSED}vh - ${dynamicHeightBuffer - 10}px)`
  )

  // Calculate button container height based on components present
  const baseHeight = 60 // Single button height
  const statusHeight = statusComponent ? 60 : 0 // Height for status component
  const widenButtonHeight = isOpen ? 60 : 0 // Height for widen button when open
  const totalHeight = baseHeight + statusHeight + widenButtonHeight

  const buttonContainerHeight = useSpringValue(totalHeight)

  useEffect(() => {
    // useEffect hook keeps the update animations in sync.
    const targetWidth = isWidened ? DRAWER_WIDTH_WIDENED : DRAWER_WIDTH
    buttonTransform.start(
      isOpen
        ? `translateX(-${targetWidth + PADDING_RIGHT - OPEN_BUTTON_OFFSET}px)`
        : `translateX(${OPEN_BUTTON_OFFSET}px)`
    )
    chevronRotation.start(isOpen ? 0 : 180)
  }, [isOpen, isWidened, buttonTransform, chevronRotation])

  useEffect(() => {
    // Animate height change when expanded state changes
    const dynamicHeightBuffer = HEIGHT_BUFFER + (headerComponent ? HEADER_HEIGHT : 0)
    const newHeight = isExpanded
      ? `calc(${VIEW_HEIGHT_EXPANDED}vh - ${dynamicHeightBuffer}px)`
      : `calc(${VIEW_HEIGHT_COLLAPSED}vh - ${dynamicHeightBuffer}px)`

    drawerHeight.start(newHeight)

    // Animate expansion button position
    expandButtonTop.start(
      isExpanded
        ? `calc(${DISTANCE_FROM_TOP}px + ${VIEW_HEIGHT_EXPANDED}vh - ${dynamicHeightBuffer - 10}px)`
        : `calc(${DISTANCE_FROM_TOP}px + ${VIEW_HEIGHT_COLLAPSED}vh - ${dynamicHeightBuffer - 10}px)`
    )
  }, [isExpanded, drawerHeight, headerComponent, expandButtonTop])

  useEffect(() => {
    // Animate button container height when drawer opens/closes or status component changes
    const baseHeight = 60 // Single button height
    const statusHeight = statusComponent ? 60 : 0 // Height for status component
    const widenButtonHeight = isOpen ? 60 : 0 // Height for widen button when open
    const totalHeight = baseHeight + statusHeight + widenButtonHeight

    buttonContainerHeight.start(totalHeight)
  }, [isOpen, statusComponent, buttonContainerHeight])

  useEffect(() => {
    // Animate width change when widened state changes
    animatedDrawerWidth.start(isWidened ? DRAWER_WIDTH_WIDENED : DRAWER_WIDTH)
  }, [isWidened, animatedDrawerWidth])

  // Handle spacebar key press for expand/collapse.
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Don't trigger if user is typing in an input, textarea, or editor.
      const activeElement = document.activeElement
      const isInInputField =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.getAttribute('contenteditable') === 'true' ||
          activeElement.closest('[contenteditable="true"]'))

      // Handle spacebar for expand/collapse (only when drawer is open).
      if (event.code === 'Space' && isOpen && !isInInputField) {
        event.preventDefault() // Prevent page scroll.
        toggleExpand()
        return
      }

      // Handle Tab for open/close drawer.
      if (event.code === 'Tab') {
        event.preventDefault() // Prevent tab from moving focus to the next element.

        // Check if focus is within the drawer.
        const drawerElement = document.querySelector('[data-drawer="notes"]')
        const focusWithinDrawer =
          drawerElement && drawerElement.contains(activeElement)

        // Only toggle drawer if focus is not within drawer or drawer is closed.
        if (!focusWithinDrawer || !isOpen) {
          // Don't prevent default here - let Tab work normally after toggling.
          toggleDrawer()
        }
      }
    }

    document.addEventListener('keydown', handleKeyPress)

    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [isOpen, isExpanded, toggleExpand, toggleDrawer])

  return (
    <>
      {/* Animated toggle button - always visible. */}
      <animated.button
        style={{
          transform: buttonTransform,
          right: OPEN_BUTTON_OFFSET,
          top: DISTANCE_FROM_TOP + 20,
          height: buttonContainerHeight,
          overflow: 'hidden',
        }}
        className='fixed bg-white border border-gray-200 rounded-l-md shadow-lg flex flex-col'
        aria-label={isOpen ? 'Close drawer' : 'Open drawer'}
      >
        {/* Combined toggle button area with chevron and status */}
        <div
          className='hover:bg-gray-50 z-50 flex flex-col cursor-pointer'
          style={{
            height: statusComponent ? 120 : 60, // 60px for chevron + 60px for status if present
          }}
          onClick={toggleDrawer}
        >
          {/* Chevron section */}
          <animated.div
            className='flex items-center justify-center p-2'
            style={{
              transform: chevronRotation.to((r) => `rotate(${r}deg)`),
              height: 60,
            }}
          >
            <ChevronsRight size={24} />
          </animated.div>

          {/* Status component section */}
          {statusComponent && (
            <div
              className='flex items-center justify-center p-2'
              style={{
                height: 60,
              }}
            >
              {statusComponent}
            </div>
          )}
        </div>

        {/* Widen button - separate with border */}
        {isOpen && (
          <div
            className='hover:bg-gray-50 z-50 flex items-center justify-center p-2 border-t border-gray-200'
            style={{
              height: 60,
            }}
            onClick={toggleWiden}
          >
            {isWidened
              ? (
                <ChevronsRightLeft size={24} />
                )
              : (
                <ChevronsLeftRight size={24} />
                )}
          </div>
        )}
      </animated.button>

      {drawerTransition(
        (style, item) =>
          item && (
            <>
              <animated.div
                style={{
                  ...style,
                  top: DISTANCE_FROM_TOP,
                  right: 10,
                  height: drawerHeight,
                  width: animatedDrawerWidth,
                }}
                className='fixed bg-white shadow-xl z-40 rounded-md border border-gray-200 flex flex-col'
              >
                {/* Optional Header */}
                {headerComponent && (
                  <div className='flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0'>
                    {headerComponent}
                    <DrawerSettingsPopover />
                  </div>
                )}

                {/* Content area - flex-grow to fill remaining space */}
                <div className='flex-1 overflow-hidden'>
                  {children}
                </div>
              </animated.div>

              {/* Expand/Collapse button - positioned below the drawer */}
              <animated.div
                style={{
                  ...style,
                  top: expandButtonTop,
                  right: 10,
                  width: animatedDrawerWidth,
                }}
                className='fixed z-40'
              >
                <div className='p-2'>
                  <button
                    style={{ height: EXPAND_BUTTON_HEIGHT }}
                    onClick={toggleExpand}
                    className='w-full flex items-center shadow-lg justify-center gap-2 px-3 py-2 text-sm font-medium border border-gray-200 text-gray-600 hover:text-gray-800 bg-white hover:bg-gray-100 rounded-md transition-colors'
                    aria-label={
                      isExpanded
                        ? 'Collapse drawer (Space) • Close drawer (Tab)'
                        : 'Expand drawer (Space) • Close drawer (Tab)'
                    }
                  >
                    {isExpanded
                      ? (
                        <>
                          <ChevronUp size={16} />
                          <span>Collapse</span>
                        </>
                        )
                      : (
                        <>
                          <ChevronDown size={16} />
                          <span>Expand</span>
                        </>
                        )}
                  </button>
                </div>
              </animated.div>
            </>
          )
      )}
    </>
  )
}
